/**
 * Centralized PO email/delivery status map.
 *
 * Single source of truth for translating a `purchase_orders.delivery_status`
 * value into a UI pill (color + label) and tooltip helper text. All surfaces
 * — dashboard vendor cards, PurchaseOrderCard pill, OrderingHub list — should
 * read from `getPoEmailStatusInfo()` so the look and copy stay aligned.
 *
 * `delivery_status` values currently written by dispatch + webhook:
 *   draft, blocked, blocked_no_email, pending_approval, approved, sending,
 *   sent, delivered, bounced, complained, opened, clicked, error, revised,
 *   confirmed, canceled, replaced, received, closed, voided, delivery_delayed
 *
 * Mapping (per task #926 — see .local/tasks/task-926.md):
 *   created   ← draft
 *   sending   ← sending, pending_approval, approved
 *   sent      ← sent
 *   delivered ← delivered, confirmed
 *   opened    ← opened
 *   clicked   ← clicked
 *   delayed   ← delivery_delayed   (orange, transient)
 *   bounced   ← bounced
 *   complained← complained
 *   failed    ← error, blocked, blocked_no_email, failed
 *   terminal  ← received, closed, canceled, voided, replaced, revised
 *               (keep their existing pill treatment — not in scope)
 *   unknown   ← everything else / missing
 */

const fmtTs = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const SENT_STATUSES = new Set(['sent', 'delivered']);
const OPENED_STATUSES = new Set(['opened', 'clicked']);
const BOUNCE_STATUSES = new Set(['bounced', 'complained', 'failed', 'error']);
const DELAY_STATUSES = new Set(['delivery_delayed']);

// ── Failure bucket ────────────────────────────────────────────────────────────
// Statuses that should drive the red-dot indicator on PO cards, dashboard
// vendor tiles, and the sidebar/home button. Lifecycle-terminal states
// (canceled, voided, replaced, revised, received, closed) are intentionally
// excluded — those are user-driven outcomes, not delivery failures.
const FAILURE_STATUSES = new Set([
  'bounced',
  'complained',
  'failed',
  'error',
  'blocked',
  'blocked_no_email',
]);

export const isFailureStatus = (status) => FAILURE_STATUSES.has(String(status || '').toLowerCase());

// Sentinel list mirrored for REST `delivery_status=in.(...)` queries used by
// the sidebar/mobile-nav failure-dot fetch.
export const FAILURE_STATUS_LIST = Array.from(FAILURE_STATUSES);

// ── Pill palette ─────────────────────────────────────────────────────────────
// Aligns with ORDER_STATUS_STYLES in app/javascript/components/ordering/
// orderingStyles.js. Keeping a parallel definition here avoids a cross-module
// import cycle (poEmailStatus is consumed by both ordering and reports code).
const PILL = {
  created:   { bg: '#FFF8E1', fg: '#6d4c00', label: 'Created' },
  sending:   { bg: '#FFF3E0', fg: '#E65100', label: 'Sending…' },
  sent:      { bg: '#E8F5E9', fg: '#2E7D32', label: 'Sent' },
  delivered: { bg: '#E8F5E9', fg: '#2E7D32', label: 'Delivered' },
  opened:    { bg: '#E3F2FD', fg: '#0d47a1', label: 'Opened' },
  clicked:   { bg: '#E3F2FD', fg: '#0d47a1', label: 'Clicked' },
  delayed:   { bg: '#FFF3CD', fg: '#856404', label: 'Delayed' },
  bounced:   { bg: '#FDECEA', fg: '#b71c1c', label: 'Bounced' },
  complained:{ bg: '#FDECEA', fg: '#b71c1c', label: 'Complained' },
  failed:    { bg: '#FDECEA', fg: '#b71c1c', label: 'Failed' },
  no_email:  { bg: '#FFF3E0', fg: '#E65100', label: 'No Email' },
  neutral:   { bg: '#F0EDED', fg: '#9A8A84', label: 'Unknown' },
};

const HELPER = {
  created:   'Draft created — not sent yet.',
  sending:   'Queued for delivery — waiting on the mail server.',
  sent:      'Sent to the vendor — not yet opened.',
  delivered: 'Delivered to the vendor’s mailbox — not yet opened.',
  opened:    'Vendor opened the email.',
  clicked:   'Vendor clicked a link in the email.',
  delayed:   'Temporary delivery delay — mail server will keep retrying.',
  bounced:   'Vendor’s mailbox bounced the email. Fix the address and resend.',
  complained:'Vendor flagged this email as spam. Reach out by another channel.',
  failed:    'Email could not be sent. Check the vendor address and resend.',
  no_email:  'No vendor email on file — add one to send this order.',
  neutral:   '',
};

/**
 * Look up the canonical pill bucket for a delivery_status.
 * Returns one of the PILL keys ('sent', 'bounced', etc.).
 */
const bucketFor = (status) => {
  const s = String(status || '').toLowerCase();
  if (!s) return 'neutral';
  if (s === 'draft') return 'created';
  if (s === 'sending' || s === 'pending_approval' || s === 'approved') return 'sending';
  if (s === 'sent') return 'sent';
  if (s === 'delivered' || s === 'confirmed') return 'delivered';
  if (s === 'opened') return 'opened';
  if (s === 'clicked') return 'clicked';
  if (s === 'delivery_delayed') return 'delayed';
  if (s === 'bounced') return 'bounced';
  if (s === 'complained') return 'complained';
  if (s === 'blocked_no_email') return 'no_email';
  if (s === 'failed' || s === 'error' || s === 'blocked') return 'failed';
  return 'neutral';
};

/**
 * Get the pill definition (bg, fg, label, helper) for any delivery_status.
 * Used by surfaces that just want a pill, without all the per-event tooltip
 * lines (e.g. dashboard recent-PO rows).
 */
export const getPoStatusPill = (status) => {
  const key = bucketFor(status);
  const pill = PILL[key] || PILL.neutral;
  return {
    bucket: key,
    bg: pill.bg,
    fg: pill.fg,
    label: pill.label,
    helper: HELPER[key] || '',
    isFailure: FAILURE_STATUSES.has(String(status || '').toLowerCase()),
  };
};

export const getPoEmailStatusInfo = (po = {}) => {
  const status = po.deliveryStatus || po.delivery_status || '';
  const sentAt = po.sentAt ?? po.sent_at;
  const deliveredAt = po.deliveredAt ?? po.delivered_at;
  const openedAt = po.openedAt ?? po.opened_at;
  const clickedAt = po.clickedAt ?? po.clicked_at;
  const bouncedAt = po.bouncedAt ?? po.bounced_at;
  const complainedAt = po.complainedAt ?? po.complained_at;
  const delayedAt = po.delayedAt ?? po.delayed_at;

  const sentStr = fmtTs(sentAt);
  const deliveredStr = fmtTs(deliveredAt);
  const openedStr = fmtTs(openedAt || clickedAt);
  const bouncedStr = fmtTs(bouncedAt);
  const complainedStr = fmtTs(complainedAt);
  const delayedStr = fmtTs(delayedAt);

  const pill = getPoStatusPill(status);
  const base = {
    deliveryStatus: status,
    pill,
    isFailure: pill.isFailure,
  };

  if (status === 'bounced' || bouncedAt) {
    return {
      ...base,
      variant: 'red',
      icon: '✉',
      iconBadge: '✗',
      label: 'Bounced',
      tooltipLines: [
        sentStr ? `Sent: ${sentStr}` : null,
        bouncedStr ? `Bounced: ${bouncedStr}` : null,
      ].filter(Boolean),
    };
  }

  if (status === 'complained' || complainedAt) {
    return {
      ...base,
      variant: 'red',
      icon: '✉',
      iconBadge: '✗',
      label: 'Complained',
      tooltipLines: [
        sentStr ? `Sent: ${sentStr}` : null,
        complainedStr ? `Complaint received: ${complainedStr}` : null,
      ].filter(Boolean),
    };
  }

  if (BOUNCE_STATUSES.has(status)) {
    return {
      ...base,
      variant: 'red',
      icon: '✉',
      iconBadge: '✗',
      label: status === 'failed' ? 'Failed' : 'Error',
      tooltipLines: [
        sentStr ? `Sent: ${sentStr}` : null,
      ].filter(Boolean),
    };
  }

  if (DELAY_STATUSES.has(status) || (delayedAt && !openedAt && !clickedAt && !bouncedAt)) {
    return {
      ...base,
      variant: 'orange',
      icon: '✉',
      iconBadge: '!',
      label: 'Delayed',
      tooltipLines: [
        sentStr ? `Sent: ${sentStr}` : null,
        delayedStr ? `Delayed since: ${delayedStr}` : null,
        'Temporary delivery delay — mail server will keep retrying.',
      ].filter(Boolean),
    };
  }

  if (OPENED_STATUSES.has(status) || openedAt || clickedAt) {
    return {
      ...base,
      variant: 'open',
      icon: '✉',
      iconBadge: '✓',
      label: 'Opened',
      tooltipLines: [
        sentStr ? `Sent: ${sentStr}` : null,
        deliveredStr ? `Delivered: ${deliveredStr}` : null,
        openedStr ? `Opened: ${openedStr}` : null,
      ].filter(Boolean),
    };
  }

  if (SENT_STATUSES.has(status)) {
    return {
      ...base,
      variant: 'closed',
      icon: '✉',
      iconBadge: null,
      label: status === 'delivered' ? 'Delivered' : 'Sent',
      tooltipLines: [
        sentStr ? `Sent: ${sentStr}` : null,
        deliveredStr ? `Delivered: ${deliveredStr}` : null,
        'Not yet opened',
      ].filter(Boolean),
    };
  }

  return {
    ...base,
    variant: 'neutral',
    icon: '○',
    iconBadge: null,
    label: status || 'No email',
    tooltipLines: [],
  };
};
